<template lang="pug">
el-dialog(
  v-model="visible",
  :title="$t('uploadDocument')",
  width="90vw",
  :before-close="handleBeforeClose"
)
  .text-bold.text-slate-600(v-if="files.length === 0") {{ $t("addFilesDialogHelper") }}

  el-table.mt-4(v-if="files.length > 0", :data="files", table-layout="auto")
    el-table-column(:label="$t('file')", min-width="200")
      template(#default="{ row }")
        .flex.items-center.min-w-0
          span.truncate {{ row.name }}
          span.text-slate-400.shrink-0.ml-1.text-sm ({{ formatFileSize(row.size) }})

    el-table-column(
      v-if="!uploadStarted",
      :label="$t('description')",
      min-width="250"
    )
      template(#default="{ row }")
        el-input(
          :model-value="getFileDescription(getFileKey(row))",
          :disabled="uploadLoading",
          @update:model-value="updateFileDescription(row, $event)"
        )

    el-table-column(
      v-if="!uploadStarted && props.documentTypes.length > 0",
      :label="$t('category')",
      min-width="500"
    )
      template(#default="{ row }")
        el-select(
          :model-value="getFileCategory(getFileKey(row))",
          :disabled="uploadLoading",
          style="width: 100%",
          @update:model-value="updateFileCategory(row, $event)"
        )
          el-option(
            v-for="documentType in props.documentTypes",
            :key="documentType.category",
            :label="$t(documentType.title)",
            :value="documentType.category"
          )

    el-table-column(
      v-if="uploadStarted",
      :label="$t('upload')",
      min-width="200"
    )
      template(#default="{ row }")
        el-progress(
          v-if="uploadStatusMap.get(getFileKey(row)) === 'uploading'",
          :percentage="Number(uploadProgressMap.get(getFileKey(row)) ?? 0)"
        )
        .flex.items-center.gap-2.text-green-600(
          v-else-if="uploadStatusMap.get(getFileKey(row)) === 'success'"
        )
          el-icon
            CircleCheckFilled
        .flex.flex-row.items-center.gap-4(
          v-else-if="uploadStatusMap.get(getFileKey(row)) === 'failed'"
        )
          el-alert(
            type="error",
            :title="$t('uploadFailed')",
            :closable="false"
          )
          el-button(
            type="danger",
            size="small",
            :disabled="uploadLoading",
            @click="retryFile(row)"
          )
            el-icon
              RefreshRight
            | {{ $t("retry") }}

  .twin-document-file-drop.flex.text-slate-700.justify-center.items-center.text-xl.mt-4(
    :class="{ 'opacity-50': isDragOver, 'opacity-100': !isDragOver, 'p-16': files.length === 0, 'p-4': files.length > 0 }",
    :style="files.length > 0 ? 'height: 100px' : ''",
    @drop="onFileDrop",
    @dragover="onDragOver",
    @dragenter="onDragEnter",
    @dragleave="onDragLeave",
    @click="openFileDialog"
  ) {{ $t("dropFilesHere") }}
  input.twin-document-upload(
    ref="fileInput",
    type="file",
    hidden,
    accept="*",
    multiple,
    @change="onFileChange($event)"
  )

  template(#footer)
    el-button(@click="closeDialog") {{ $t("cancel") }}
    el-button(
      v-if="!uploadStarted",
      type="primary",
      :disabled="uploadDisabled",
      @click="uploadDocuments"
    ) {{ $t("upload") }}
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { ElMessageBox, ElNotification } from "element-plus";
import axios from "@shared/services/axiosProvider";
import type { AxiosProgressEvent } from "axios";
import { useTwinDocumentsStore } from "@shared/stores/documents";

const props = defineProps<{
  visible: boolean;
  twinId: string;
  category: string;
  documentTypes: Array<{ title: string; category: string }>;
}>();

const emit = defineEmits<{
  "update:visible": [visible: boolean];
  uploaded: [];
}>();

const { t } = useI18n();
const documentsStore = useTwinDocumentsStore();

const visible = computed({
  get: () => props.visible,
  set: (value: boolean) => emit("update:visible", value),
});

const files = ref<File[]>([]);
const fileDescriptions = reactive(new Map<string, string>());
const fileCategories = reactive(new Map<string, string>());
const uploadProgressMap = reactive(new Map<string, string>());
const uploadStatusMap = reactive(
  new Map<string, "uploading" | "success" | "failed">(),
);
const uploadStarted = ref(false);
const uploadAbortController = ref<AbortController | null>(null);
const isDragOver = ref(false);
const dragDepth = ref(0);
const fileInput = ref<HTMLInputElement | null>(null);

const uploadLoading = computed(() =>
  files.value.some(
    (file) => uploadStatusMap.get(getFileKey(file)) === "uploading",
  ),
);
const uploadDisabled = computed(
  () =>
    files.value.length === 0 ||
    uploadLoading.value ||
    files.value.some((file) => getFileDescription(getFileKey(file)) === ""),
);

watch(
  () => visible.value,
  (isVisible) => {
    if (isVisible) {
      resetDialog();
    }
  },
);

function resetDialog() {
  files.value = [];
  fileDescriptions.clear();
  fileCategories.clear();
  uploadProgressMap.clear();
  uploadStatusMap.clear();
  uploadStarted.value = false;
  uploadAbortController.value = null;
  isDragOver.value = false;
  dragDepth.value = 0;
}

function getFileKey(file: Pick<File, "name" | "size">) {
  return `${file.name}::${file.size}`;
}

function getFileDescription(key: string) {
  return fileDescriptions.get(key) ?? "";
}

function setFileDescription(key: string, value: string) {
  fileDescriptions.set(key, value);
}

function updateFileDescription(file: File, value: string) {
  setFileDescription(getFileKey(file), value);
}

function getFileCategory(key: string) {
  return fileCategories.get(key) ?? props.category;
}

function setFileCategory(key: string, value: string) {
  fileCategories.set(key, value);
}

function updateFileCategory(file: File, value: string) {
  setFileCategory(getFileKey(file), value);
}

function openFileDialog() {
  fileInput.value?.click();
}

function notifyDuplicateFiles(duplicateFiles: File[]) {
  const names = [...new Set(duplicateFiles.map((file) => file.name))].join(
    ", ",
  );
  ElNotification({
    type: "warning",
    message: t("documentsAlreadyInList", { names }),
  });
}

function addFiles(addedFiles: File[]) {
  const existingFileKeys = new Set(files.value.map((file) => getFileKey(file)));
  const seenAddedFileKeys = new Set<string>();
  const newFiles: File[] = [];
  const duplicateFiles: File[] = [];

  addedFiles.forEach((file) => {
    const key = getFileKey(file);
    if (existingFileKeys.has(key) || seenAddedFileKeys.has(key)) {
      duplicateFiles.push(file);
      return;
    }

    seenAddedFileKeys.add(key);
    newFiles.push(file);
    fileDescriptions.set(key, file.name);
    fileCategories.set(key, props.category);
  });

  files.value.push(...newFiles);

  if (duplicateFiles.length > 0) {
    notifyDuplicateFiles(duplicateFiles);
  }
}

function onFileDrop(event: DragEvent) {
  event.preventDefault();
  dragDepth.value = 0;
  isDragOver.value = false;
  if (event.dataTransfer?.files.length) {
    addFiles(Array.from(event.dataTransfer.files));
  }
}

function onDragEnter(event: DragEvent) {
  event.preventDefault();
  dragDepth.value += 1;
  isDragOver.value = true;
}

function onDragLeave(event: DragEvent) {
  event.preventDefault();
  dragDepth.value = Math.max(0, dragDepth.value - 1);
  isDragOver.value = dragDepth.value > 0;
}

function onDragOver(event: DragEvent) {
  event.preventDefault();
}

function onFileChange(event: Event) {
  const input = event.target as HTMLInputElement;
  if (input.files) {
    addFiles(Array.from(input.files));
  }
  input.value = "";
}

function onUploadProgress(key: string, progressEvent: AxiosProgressEvent) {
  uploadProgressMap.set(key, (100 * (progressEvent.progress ?? 0)).toFixed(1));
}

async function uploadSingleFile(file: File, signal: AbortSignal) {
  const key = getFileKey(file);
  uploadStatusMap.set(key, "uploading");
  uploadProgressMap.set(key, "0");

  try {
    await documentsStore.addTwinDocument(
      props.twinId,
      file,
      {
        category: getFileCategory(key),
        description: getFileDescription(key),
      },
      (progressEvent) => onUploadProgress(key, progressEvent),
      signal,
    );
    uploadStatusMap.set(key, "success");
  } catch (error) {
    if (axios.isCancel(error)) {
      throw error;
    }
    uploadStatusMap.set(key, "failed");
    throw error;
  }
}

async function uploadDocuments() {
  if (uploadDisabled.value) {
    return;
  }

  uploadStarted.value = true;
  const abortController = new AbortController();
  uploadAbortController.value = abortController;

  const results = await Promise.allSettled(
    files.value.map((file) => uploadSingleFile(file, abortController.signal)),
  );
  uploadAbortController.value = null;

  if (abortController.signal.aborted) {
    return;
  }

  if (results.some((result) => result.status === "rejected")) {
    ElNotification({
      type: "error",
      message: t("genericError"),
    });
    return;
  }

  visible.value = false;
  emit("uploaded");
}

async function retryFile(file: File) {
  const abortController = new AbortController();
  uploadAbortController.value = abortController;

  await uploadSingleFile(file, abortController.signal).catch((error) => {
    if (!axios.isCancel(error)) {
      ElNotification({
        type: "error",
        message: t("genericError"),
      });
    }
  });

  if (abortController.signal.aborted) {
    return;
  }

  uploadAbortController.value = null;

  if (
    files.value.every(
      (currentFile) =>
        uploadStatusMap.get(getFileKey(currentFile)) === "success",
    )
  ) {
    visible.value = false;
    emit("uploaded");
  }
}

async function canCloseDialog() {
  if (uploadLoading.value) {
    try {
      await ElMessageBox.confirm(
        t("cancelUploadsConfirm"),
        t("cancelUploads"),
        {
          confirmButtonText: t("cancelUploadsYes"),
          cancelButtonText: t("cancelUploadsNo"),
          type: "warning",
        },
      );
    } catch {
      return false;
    }
  }

  uploadAbortController.value?.abort();
  uploadAbortController.value = null;
  return true;
}

async function handleBeforeClose(done: () => void) {
  if (await canCloseDialog()) {
    done();
  }
}

async function closeDialog() {
  if (await canCloseDialog()) {
    visible.value = false;
  }
}

function formatFileSize(bytes: number) {
  let size = bytes;
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < 3) {
    size /= 1024;
    unitIndex++;
  }

  const unit =
    unitIndex === 0
      ? "B"
      : unitIndex === 1
        ? "KB"
        : unitIndex === 2
          ? "MB"
          : "GB";
  return `${size.toFixed(2)} ${unit}`;
}
</script>

<style scoped>
.twin-document-file-drop {
  border-radius: 8px;
  border-style: dashed;
  border-color: var(--el-border-color);
  border-width: 4px;
}
</style>
